// NOSONAR
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// Use toast.warning for notifications (assume globally available or imported)
function showToast(message) {
  if (window.toast && typeof window.toast.warning === 'function') {
    window.toast.warning(message);
  } else if (typeof toast !== 'undefined' && typeof toast.warning === 'function') {
    toast.warning(message);
  }
}

const DatePicker = ({ value, onChange, label, required = false, disableFutureDates = false }) => {

  const { stage, subStage } = useSelector(state => state.eligibility)
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  // latest values for scroll handlers (avoid stale closures)
  const selectedDayRef = useRef(selectedDay);
  const selectedMonthRef = useRef(selectedMonth);
  const selectedYearRef = useRef(selectedYear);
  useEffect(() => { selectedDayRef.current = selectedDay; }, [selectedDay]);
  useEffect(() => { selectedMonthRef.current = selectedMonth; }, [selectedMonth]);
  useEffect(() => { selectedYearRef.current = selectedYear; }, [selectedYear]);

  // Raw data
  // Helper to get days in a month/year
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };

  // Compute days for selected month/year
  const React = require('react');
  const { useMemo } = React;
  const daysRaw = useMemo(() => {
    const daysCount = getDaysInMonth(selectedMonth, selectedYear);
    return Array.from({ length: daysCount }, (_, i) => String(i + 1).padStart(2, '0'));
  }, [selectedMonth, selectedYear]);
  const monthsRaw = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const yearsRaw = Array.from({ length: 100 }, (_, i) => String(currentYear - i));

  // Buffered lists for scroll columns
  const [days, setDays] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [buffer, setBuffer] = useState(3);

  // Debounce per column when auto-selecting on scroll
  const debounceTimers = useRef({ day: null, month: null, year: null });

  // Initialize from prop or today
  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-');
      setSelectedDay(d);
      setSelectedMonth(m);
      setSelectedYear(y);
    } else {
      setSelectedDay('');
      setSelectedMonth('');
      setSelectedYear('');
    }
  }, [value]);

  // On open, if no value is selected, preselect the maximum valid date (minDate)
  useEffect(() => {
    if (isOpen && !selectedDay && !selectedMonth && !selectedYear) {
      setSelectedDay(String(minDate.getDate()).padStart(2, '0'));
      setSelectedMonth(String(minDate.getMonth() + 1).padStart(2, '0'));
      setSelectedYear(String(minDate.getFullYear()));
    }
  }, [isOpen]);

  // Build buffered arrays when opened or when daysRaw/monthsRaw/yearsRaw change
  useEffect(() => {
    if (!isOpen) return;

    // compute buffer based on first available column
    let bufferSize = 7;
    const pickRef = [dayRef, monthRef, yearRef].find(r => r.current);
    if (pickRef?.current) {
      const firstItem = pickRef.current.querySelector('.date-item[data-value]');
      if (firstItem) {
        const h = firstItem.offsetHeight;
        const ch = pickRef.current.offsetHeight;
        if (h && ch) bufferSize = Math.ceil(ch / h / 2) + 3;
      }
    }

    setBuffer(bufferSize);
    const head = Math.max(bufferSize, 0);
    const tail = Math.max(bufferSize, 0);

    setDays([...Array(head).fill(''), ...daysRaw, ...Array(tail).fill('')]);
    setMonths([
      ...Array(head).fill(null).map(() => ({ value: '', label: '' })), // unique empties
      ...monthsRaw,
      ...Array(tail).fill(null).map(() => ({ value: '', label: '' }))
    ]);
    setYears([...Array(head).fill(''), ...yearsRaw, ...Array(tail).fill('')]);
  }, [isOpen, daysRaw, monthsRaw, yearsRaw]);

  // Disabled logic (unified)
  // Minimum age logic: must be at least 18 years ago
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 18);
  minDate.setHours(0, 0, 0, 0);

  const isDisabled = {
    day: (d, m, y) => {
      if (!disableFutureDates) return false;
      const sel = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
      if (disableFutureDates && sel > todayEnd) return true;
      return false;
    },
    month: (m, y) => {
      if (!disableFutureDates) return false;
      const yy = parseInt(y), mm = parseInt(m);
      if (disableFutureDates && (yy > currentYear || (yy === currentYear && mm > currentMonth))) return true;
      return false;
    },
    year: (y) => {
      if (!disableFutureDates) return false;
      if (disableFutureDates && parseInt(y) > currentYear) return true;
      return false;
    }
  };

  // Update outward value (clamps day to month length)
  const updateDate = (d, m, y) => {
    if (!d || !m || !y) return;
    const daysInMonth = new Date(parseInt(y), parseInt(m), 0).getDate();
    const validDay = Math.min(parseInt(d), daysInMonth);
    const dd = String(validDay).padStart(2, '0');
    onChange(`${y}-${m}-${dd}`);
  };

  // Unified manual select (click/keyboard)
  const handleSelect = (type, value) => {
    let disabled = false;
    if (type === 'day' && isDisabled.day(value, selectedMonth, selectedYear)) disabled = true;
    if (type === 'month' && isDisabled.month(value, selectedYear)) disabled = true;
    if (type === 'year' && isDisabled.year(value)) disabled = true;

    if (disabled) {
      showToast('Future dates are not allowed.');
      return;
    }

    // Set selection
    if (type === 'day') setSelectedDay(value);
    if (type === 'month') setSelectedMonth(value);
    if (type === 'year') setSelectedYear(value);

    // Compute the full date after updating selection
    let d = type === 'day' ? value : selectedDay;
    let m = type === 'month' ? value : selectedMonth;
    let y = type === 'year' ? value : selectedYear;
    if (d && m && y) {
      const daysInMonth = new Date(parseInt(y), parseInt(m), 0).getDate();
      const validDay = Math.min(parseInt(d), daysInMonth);
      const dd = String(validDay).padStart(2, '0');
      const selectedDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(dd));
      if (selectedDate > minDate) {
        showToast('Minimum age should be 18 years. Please select a valid date.');
        return;
      }
      onChange(`${y}-${m}-${dd}`);
    }
  };

  const handleKeyDownOpen = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  // Center a chosen value in its scroll column
  const scrollToCenter = (ref, value) => {
    if (!ref?.current || !value) return;
    const scroller = ref.current.querySelector('.column-scroll');
    if (!scroller) return;
    const el = scroller.querySelector(`.date-item[data-value="${value}"]`);
    if (!el) return;
    scroller.scrollTop = el.offsetTop - (scroller.clientHeight / 2) + (el.offsetHeight / 2);
  };

  // Auto-select the closest item to center while scrolling (with tiny debounce)
  const applyScrollEffects = (ref, type) => {
    if (!ref?.current) return;
    const scroller = ref.current.querySelector('.column-scroll');
    if (!scroller) return;

    const items = Array.from(scroller.querySelectorAll('.date-item'))
      .filter(node => node.getAttribute('data-value'));

    const center = scroller.scrollTop + scroller.clientHeight / 2;

    // Find the closest non-disabled item to the center
    let closest = null;
    let minDist = Infinity;
    items.forEach(item => {
      const value = item.getAttribute('data-value');
      let disabled = false;
      if (type === 'day') {
        disabled = isDisabled.day(value, selectedMonthRef.current, selectedYearRef.current);
      } else if (type === 'month') {
        disabled = isDisabled.month(value, selectedYearRef.current);
      } else if (type === 'year') {
        disabled = isDisabled.year(value);
      }
      if (disabled) return;
      const itemCenter = item.offsetTop + item.offsetHeight / 2;
      const dist = Math.abs(itemCenter - center);
      if (dist < minDist) { minDist = dist; closest = item; }
    });

    if (!closest) return;
    const value = closest.getAttribute('data-value');

    if (debounceTimers.current[type]) clearTimeout(debounceTimers.current[type]);
    debounceTimers.current[type] = setTimeout(() => {
      if (type === 'day') {
        if (value !== selectedDayRef.current) {
          setSelectedDay(value);
          updateDate(value, selectedMonthRef.current, selectedYearRef.current);
        }
      } else if (type === 'month') {
        if (value !== selectedMonthRef.current) {
          setSelectedMonth(value);
          updateDate(selectedDayRef.current, value, selectedYearRef.current);
        }
      } else if (type === 'year') {
        if (value !== selectedYearRef.current) {
          setSelectedYear(value);
          updateDate(selectedDayRef.current, selectedMonthRef.current, value);
        }
      }
    }, 100);
  };

  // Attach scroll listeners when open (and after lists exist)
  useEffect(() => {
    if (!isOpen) return;
    const cfg = [
      { ref: dayRef, type: 'day' },
      { ref: monthRef, type: 'month' },
      { ref: yearRef, type: 'year' }
    ];

    const cleanups = [];
    cfg.forEach(({ ref, type }) => {
      const scroller = ref.current?.querySelector('.column-scroll');
      if (!scroller) return;

      const onScroll = () => applyScrollEffects(ref, type);
      // run once to align initial visual state
      setTimeout(onScroll, 0);

      scroller.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => scroller.removeEventListener('scroll', onScroll));
    });

    return () => cleanups.forEach(fn => fn && fn());
  }, [isOpen, days.length, months.length, years.length]); // rebind when lists populate

  // On open (and when selection changes), center the selected values
  useEffect(() => {
    if (!isOpen) return;
    // Wait for next paint to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        scrollToCenter(dayRef, selectedDayRef.current);
        scrollToCenter(monthRef, selectedMonthRef.current);
        scrollToCenter(yearRef, selectedYearRef.current);
      }, 0);
    });
  }, [isOpen, selectedDay, selectedMonth, selectedYear, days.length, months.length, years.length]);

  const formatDisplayDate = () => {
    if (selectedDay && selectedMonth && selectedYear) {
      const monthName = monthsRaw.find(m => m.value === selectedMonth)?.label || '';
      return `${selectedDay} ${monthName} ${selectedYear}`;
    }
    return 'Select Date';
  };

  const renderColumnItems = (type, items, selected) => {
    return items.map((item, i) => {
      const value = type === 'month' ? (item?.value || '') : (item || '');
      const label = type === 'month' ? (item?.label || '') : (item || '');
      const isSelected = value === selected;
      const disabled = value ? (
        type === 'day' ? isDisabled.day(value, selectedMonth, selectedYear)
          : type === 'month' ? isDisabled.month(value, selectedYear)
            : isDisabled.year(value)
      ) : false;

      return (
        <div
          key={`${type}-${i}`}
          className={`date-item ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
          data-value={value}
          role="option"
          aria-selected={isSelected}
          tabIndex={isSelected ? 0 : -1}
          onClick={() => !disabled && value && handleSelect(type, value)}
          onKeyDown={(e) => !disabled && value && (e.key === 'Enter' || e.key === ' ') && handleSelect(type, value)}
        >
          {label || ' '}
        </div>
      );
    });
  };

  const columns = [
    { type: 'day', ref: dayRef, items: days, selected: selectedDay, aria: 'Days' },
    { type: 'month', ref: monthRef, items: months, selected: selectedMonth, aria: 'Months' },
    { type: 'year', ref: yearRef, items: years, selected: selectedYear, aria: 'Years' }
  ];

  // Check if selected date is less than 18 years ago (age < 18)
  let isUnder18 = false;
  if (selectedDay && selectedMonth && selectedYear) {
    const daysInMonth = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
    const validDay = Math.min(parseInt(selectedDay), daysInMonth);
    const dd = String(validDay).padStart(2, '0');
    const selectedDate = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, parseInt(dd));
    if (selectedDate > minDate) {
      isUnder18 = true;
    }
  }

  // Helper to call onChange with current selection if valid
  const handleConfirm = () => {
    if (selectedDay && selectedMonth && selectedYear && !isUnder18) {
      const daysInMonth = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
      const validDay = Math.min(parseInt(selectedDay), daysInMonth);
      const dd = String(validDay).padStart(2, '0');
      const formatted = `${selectedYear}-${selectedMonth}-${dd}`;
      if (typeof onChange === 'function') {
        onChange(formatted);
      }
    }
    setIsOpen(false);
  };

  return (
    <>
      <div className="custom-date-picker">
        <div
          className="form-control date-picker-input input-underline custom-dashboard-date"
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKeyDownOpen}
          tabIndex={0}
          role="button"
          aria-label="Open date picker"
          style={{ cursor: 'pointer' }}
        >
          <span className="small fs-14">{formatDisplayDate()}</span>
          <i className="icon-timecalendar01 float-end"></i>
        </div>
      </div>

      {isOpen && (
        <div
          id="datepicker-dialog"
          role="dialog"
          aria-modal="true"
          className="datepicker-overlay"
          onClick={() => setIsOpen(false)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          <div
            className="date-picker-modal"
            onClick={(e) => e.stopPropagation()}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <div className="date-picker-header">
              <h6>Select Date</h6>
              <button type="button" className="btn-close" onClick={() => setIsOpen(false)}></button>
            </div>

            <div className="datepicker-container" role="document" tabIndex={0} onKeyDown={(e) => e.stopPropagation()}>
              <div className="columns-container">
                {columns.map(({ type, ref, items, selected, aria }) => (
                  <div className="column" ref={ref} key={type}>
                    <div className="column-scroll" tabIndex={0} role="listbox" aria-label={aria}>
                      {renderColumnItems(type, items, selected)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="date-picker-footer">
              <button type="button" className="btn btn-primary w-100" onClick={handleConfirm} disabled={isUnder18}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DatePicker;